import {
  boardCopyResultSchema,
  boardTemplateSchema,
  createBoardFromTemplateBodySchema,
  templateDescriptionPatchSchema,
  templateCategoryPatchSchema,
  templateVisibilityPatchSchema,
  type ApiError,
  type Board,
  type BoardCopyResult,
  type BoardTemplate,
  type CreateBoardFromTemplateBody,
  type TemplateCategoryPatch,
  type TemplateVisibilityPatch,
} from '@molt/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { announce } from './announce';
import { del, get, patch, post } from './api';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export interface CreateBoardFromTemplateVariables {
  readonly templateBoardId: string;
  readonly templateTitle: string;
  readonly workspaceId: string;
  readonly visibility: 'private' | 'workspace_visible' | 'public';
  readonly title: string;
  readonly keepCards: boolean;
}

export function getBoardTemplate(boardId: string): Promise<BoardTemplate> {
  return get(`/boards/${encodeURIComponent(boardId)}/template`, boardTemplateSchema);
}

export function markBoardTemplate(boardId: string): Promise<BoardTemplate> {
  return post(`/boards/${encodeURIComponent(boardId)}/template`, boardTemplateSchema);
}

export function unmarkBoardTemplate(boardId: string): Promise<BoardTemplate> {
  return del(`/boards/${encodeURIComponent(boardId)}/template`, boardTemplateSchema);
}

export function updateTemplateDescription(
  boardId: string,
  description: string | null,
): Promise<BoardTemplate> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/template-description`,
    boardTemplateSchema,
    templateDescriptionPatchSchema.parse({ description }),
  );
}

export function updateTemplateVisibility(
  boardId: string,
  body: TemplateVisibilityPatch,
): Promise<BoardTemplate> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/template-visibility`,
    boardTemplateSchema,
    templateVisibilityPatchSchema.parse(body),
  );
}

export function updateTemplateCategory(
  boardId: string,
  body: TemplateCategoryPatch,
): Promise<BoardTemplate> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/template-category`,
    boardTemplateSchema,
    templateCategoryPatchSchema.parse(body),
  );
}

export function createBoardFromTemplate(
  boardId: string,
  body: CreateBoardFromTemplateBody,
): Promise<BoardCopyResult> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/create-from-template`,
    boardCopyResultSchema,
    createBoardFromTemplateBodySchema.parse(body),
  );
}

export function boardTemplateQueryOptions(
  boardId: string,
): UseQueryOptions<BoardTemplate, ApiError> {
  return {
    queryKey: queryKeys.boards.template(boardId),
    queryFn: () => getBoardTemplate(boardId),
  };
}

export function useBoardTemplate(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardTemplate, ApiError> {
  return useQuery({
    ...boardTemplateQueryOptions(boardId),
    enabled,
  });
}

export function useMarkBoardTemplate(
  boardId: string,
): UseMutationResult<BoardTemplate, ApiError, void> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: () => markBoardTemplate(boardId),
    onSuccess: (template) => {
      writeBoardTemplateSuccess(queryClient, boardId, template);
    },
    onError: () => {
      announce(t('boardTemplateHooks.markFailed'));
    },
  });
}

export function useUnmarkBoardTemplate(
  boardId: string,
): UseMutationResult<BoardTemplate, ApiError, void> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: () => unmarkBoardTemplate(boardId),
    onSuccess: (template) => {
      writeBoardTemplateSuccess(queryClient, boardId, template);
    },
    onError: () => {
      announce(t('boardTemplateHooks.unmarkFailed'));
    },
  });
}

export function useUpdateTemplateDescription(
  boardId: string,
): UseMutationResult<BoardTemplate, ApiError, { description: string | null }> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ description }: { description: string | null }) =>
      updateTemplateDescription(boardId, description),
    onSuccess: (template) => {
      writeBoardTemplateSuccess(queryClient, boardId, template);
    },
    onError: () => {
      announce(t('boardTemplateHooks.descriptionFailed'));
    },
  });
}

export function useUpdateTemplateVisibility(
  boardId: string,
): UseMutationResult<BoardTemplate, ApiError, TemplateVisibilityPatch> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (body: TemplateVisibilityPatch) =>
      updateTemplateVisibility(boardId, body),
    onSuccess: (template) => {
      writeBoardTemplateSuccess(queryClient, boardId, template);
    },
    onError: () => {
      announce(t('boardTemplateHooks.visibilityFailed'));
    },
  });
}

export function useUpdateTemplateCategory(
  boardId: string,
): UseMutationResult<BoardTemplate, ApiError, TemplateCategoryPatch> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (body: TemplateCategoryPatch) => updateTemplateCategory(boardId, body),
    onSuccess: (template) => {
      writeBoardTemplateSuccess(queryClient, boardId, template);
    },
    onError: () => {
      announce(t('boardTemplateHooks.categoryFailed'));
    },
  });
}

export function useCreateBoardFromTemplate(): UseMutationResult<
  BoardCopyResult,
  ApiError,
  CreateBoardFromTemplateVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables: CreateBoardFromTemplateVariables) =>
      createBoardFromTemplate(variables.templateBoardId, {
        workspaceId: variables.workspaceId,
        visibility: variables.visibility,
        title: variables.title,
        keepCards: variables.keepCards,
      }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(queryKeys.boards.detail(result.board.id), result.board);
      queryClient.setQueriesData<Board[]>(
        { queryKey: queryKeys.workspaces.boards(result.board.workspaceId, 'open') },
        (previous) => appendBoardIfMissing(previous, result.board),
      );
      invalidateTemplateSuccess(
        queryClient,
        variables.templateBoardId,
        variables.workspaceId,
      );
    },
    onError: (_error, variables) => {
      announce(t('boardTemplateHooks.createFailed', { title: variables.templateTitle }));
    },
  });
}

function writeBoardTemplateSuccess(
  queryClient: QueryClient,
  boardId: string,
  template: BoardTemplate,
): void {
  queryClient.setQueryData(queryKeys.boards.template(boardId), template);
  invalidateTemplateSuccess(queryClient, boardId, template.workspaceId);
}

function invalidateTemplateSuccess(
  queryClient: QueryClient,
  boardId: string,
  workspaceId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.template(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.templates.all() });
  void queryClient.invalidateQueries({
    queryKey: ['workspaces', workspaceId, 'boards'],
  });
}

function appendBoardIfMissing(
  previous: Board[] | undefined,
  board: Board,
): Board[] | undefined {
  if (previous === undefined) return undefined;
  if (previous.some((entry) => entry.id === board.id)) return previous;
  return [...previous, board];
}
